import { colorFields } from "./_shared";
/**
 * Marquee band — a full-bleed strip of large type that scrolls sideways
 * forever ("LIMITED EDITION COLLECTION · LIMITED EDITION COLLECTION · …").
 *
 * Ported from the EasyOrders `ab-marquee-section` found on the `dark` family
 * (2026-07-20), which ships TWO of them — one Latin, one Arabic scrolling the
 * other way — as in-flow separators between the hero, the category rail and
 * the first product row.
 *
 * A NEW type rather than a variant, and the alternatives were checked:
 *
 *  - `announcement-bar` / `ann-marquee` is the obvious candidate and it CANNOT
 *    express this. That section is chrome: `BuyerShell` renders it above the
 *    header and its position in the section list is explicitly ignored, and it
 *    is capped at `maxPerTemplate: 1`. Dark's marquees are IN THE PAGE FLOW,
 *    at two different positions, with different copy and direction. Both the
 *    placement and the cardinality are wrong.
 *  - `collections` / `ticker` is a marquee, but of CATEGORY TILES — its content
 *    is the store's category list, not authored words.
 *
 * So the content model (an ordered list of phrases, repeated to fill) has no
 * home, and this is the third new type the workstream has added after
 * `shop-the-look` and `highlight-headline`.
 *
 * Their inline custom properties are the renderer's source — see the "dark"
 * subsection of docs/plans/2026-07-19-storefront-section-vocabulary.md.
 */
export const marqueeBandSchema = {
    type: "marquee-band",
    name: { ar: "شريط متحرك", en: "Scrolling band" },
    description: {
        ar: "شريط عريض بخط كبير بيتحرك جنبًا من غير توقف، بيفصل بين الأقسام.",
        en: "A full-bleed strip of big type that scrolls sideways, separating sections.",
    },
    // Dark ships TWO on one page; 3 leaves room without inviting a page of them.
    maxPerTemplate: 3,
    availableOnTemplates: ["home"],
    settings: [
        {
            id: "size",
            type: "select",
            label: { ar: "حجم الخط", en: "Type size" },
            helpText: {
                ar: "ارتفاع الشريط بيتبع حجم الخط.",
                en: "The band's height follows the type size.",
            },
            options: [
                { value: "small", label: { ar: "صغير", en: "Small" } },
                { value: "medium", label: { ar: "متوسط", en: "Medium" } },
                { value: "large", label: { ar: "كبير", en: "Large" } },
                // AUTHORED, not interpolated. The volta family ships this engine at
                // `--mq-font-size-dt: 180px` / `--mq-lh-dt: 150px`, 3.75× dark's
                // authored 48/68 — a display scale the three existing stops cannot
                // reach. Added 2026-07-20 with the volta theme.
                { value: "display", label: { ar: "ضخم", en: "Display" } },
            ],
            default: "medium",
        },
        {
            id: "speed",
            type: "select",
            label: { ar: "السرعة", en: "Speed" },
            helpText: {
                ar: "الوقت اللي الشريط بياخده عشان يلف لفة كاملة.",
                en: "How long one full pass of the band takes.",
            },
            options: [
                { value: "slow", label: { ar: "بطيء", en: "Slow" } },
                { value: "medium", label: { ar: "متوسط", en: "Medium" } },
                { value: "fast", label: { ar: "سريع", en: "Fast" } },
            ],
            default: "medium",
        },
        {
            id: "direction",
            type: "select",
            label: { ar: "اتجاه الحركة", en: "Direction" },
            helpText: {
                ar: "الشريط بيتحرك ناحية الشمال ولا اليمين.",
                en: "Which way the band travels.",
            },
            options: [
                { value: "start", label: { ar: "لبداية السطر", en: "Towards the start" } },
                { value: "end", label: { ar: "لنهاية السطر", en: "Towards the end" } },
            ],
            default: "start",
        },
        {
            /**
             * Their `--mq-tilt` is a free angle. Exposed as three stops instead: the
             * band is full-bleed and rotating it by an arbitrary amount opens a gap
             * at the page edge that only a wider overscan hides. Their own instance
             * ships `0deg`; the tilted stops are the engine's other documented use.
             */
            id: "tilt",
            type: "select",
            label: { ar: "الميل", en: "Tilt" },
            helpText: {
                ar: "الشريط مستقيم ولا مايل شوية.",
                en: "Keep the band level, or angle it slightly.",
            },
            options: [
                { value: "none", label: { ar: "مستقيم", en: "Level" } },
                { value: "up", label: { ar: "مايل لفوق", en: "Angled up" } },
                { value: "down", label: { ar: "مايل لتحت", en: "Angled down" } },
            ],
            default: "none",
        },
        {
            id: "separator",
            type: "select",
            label: { ar: "الفاصل", en: "Separator" },
            helpText: {
                ar: "العلامة اللي بتفصل بين الجمل.",
                en: "The mark printed between phrases.",
            },
            options: [
                { value: "dot", label: { ar: "نقطة", en: "Dot" } },
                { value: "slash", label: { ar: "شرطة", en: "Slash" } },
                { value: "star", label: { ar: "نجمة", en: "Star" } },
                { value: "none", label: { ar: "بدون", en: "None" } },
            ],
            default: "dot",
        },
        {
            /**
             * Added 2026-07-20 with the prime family, and the page-level diff is
             * what forced it. The renderer hardcoded `text-transform: uppercase`,
             * described in its own header as "large uppercase type" — which is
             * dark's engine. prime's `tsm-scroll__phrase` authors
             * `text-transform: none` EXPLICITLY, so its band reads "Welcome to our
             * store" where ours printed "WELCOME TO OUR STORE".
             *
             * Two families authoring opposite values is exactly what a control is
             * for. `upper` stays the default so no existing store changes.
             */
            id: "textCase",
            type: "select",
            label: { ar: "حالة الحروف", en: "Letter case" },
            helpText: {
                ar: "الكلام يتكتب بحروف كبيرة ولا زي ما هو.",
                en: "Print the phrases in capitals, or exactly as typed.",
            },
            options: [
                { value: "upper", label: { ar: "حروف كبيرة", en: "Capitals" } },
                { value: "none", label: { ar: "زي ما هو", en: "As typed" } },
            ],
            default: "upper",
        },
        ...colorFields(),
    ],
    blocks: [
        {
            type: "marquee-line",
            name: { ar: "جملة", en: "Phrase" },
            description: {
                ar: "جملة قصيرة بتتكرر على طول الشريط.",
                en: "A short phrase, repeated along the band.",
            },
            maxPerSection: 6,
            settings: [
                {
                    id: "text",
                    type: "text",
                    i18n: true,
                    label: { ar: "الجملة", en: "Phrase" },
                    validate: { max: 60 },
                },
            ],
        },
    ],
};
//# sourceMappingURL=marquee-band.js.map